/**
 * D-EZ-102 — THE CHAMPION'S MOMENT, AND THE YEAR IN REVIEW.
 *
 * When a season settles: the trophy presentation (the champion, the final four,
 * the race that decided it), then the year in review — the awards, the records
 * that fell, the retirements, anyone the sport lost, the Hall of Fame class —
 * before the winter's numbers. Reachable again any time from the Career tab.
 * Every line is `story/yearInReview.ts`, a fold over the settled seasons.
 */

import { useMemo } from 'react';
import type { HistorySeason } from '../../domain/history/index.ts';
import { yearInReview } from '../../domain/story/yearInReview.ts';
import { liveryForDriver } from '../../domain/livery.ts';
import { ordinal } from '../../domain/story/raceStory.ts';
import CarTopDown from '../CarTopDown.tsx';

export interface ChampionsMomentProps {
  seasons: readonly HistorySeason[];
  seasonNumber: number;
  onDriver?: (driverId: string) => void;
  /** "On to the winter" — the winter's numbers. */
  onContinue?: () => void;
  /** Read the season-in-review and winter editions of the paper. */
  onReadSeasonPaper?: () => void;
  onReadWinterPaper?: () => void;
}

/** A trophy, drawn — no file to go missing. */
function Trophy() {
  return (
    <svg className="trophy__cup" viewBox="0 0 64 72" aria-hidden="true">
      <path d="M16 6h32v14c0 11-7 19-16 19S16 31 16 20z" fill="currentColor" />
      <path d="M16 10H6v6c0 8 6 13 12 13M48 10h10v6c0 8-6 13-12 13" fill="none" stroke="currentColor" strokeWidth="4" />
      <rect x="28" y="38" width="8" height="14" fill="currentColor" />
      <rect x="18" y="52" width="28" height="7" rx="1" fill="currentColor" />
      <rect x="14" y="60" width="36" height="7" rx="1" fill="currentColor" />
    </svg>
  );
}

export default function ChampionsMoment({
  seasons,
  seasonNumber,
  onDriver,
  onContinue,
  onReadSeasonPaper,
  onReadWinterPaper,
}: ChampionsMomentProps) {
  const review = useMemo(() => yearInReview(seasons, seasonNumber), [seasons, seasonNumber]);
  const season = seasons.find((s) => s.seasonNumber === seasonNumber);
  if (!review || !season) {
    return <p className="dim">The trophy is presented when the season is settled.</p>;
  }
  const champ = review.champion;
  const card = champ ? season.roster.find((c) => c.driverId === champ.driverId) : undefined;
  const team = champ ? season.teams.find((t) => t.driverIds.includes(champ.driverId)) : undefined;
  const Name = ({ id, name }: { id: string; name: string }) =>
    onDriver ? (
      <button type="button" className="linkbtn" onClick={() => onDriver(id)}>
        {name}
      </button>
    ) : (
      <>{name}</>
    );

  return (
    <div className="ymoment">
      <section className="trophy" aria-labelledby="trophy-h">
        <p className="trophy__kicker">Season {seasonNumber} · the championship</p>
        <div className="trophy__body">
          <Trophy />
          <div className="trophy__who">
            <h2 id="trophy-h" className="trophy__name">
              {champ ? champ.name : 'No champion was crowned'}
            </h2>
            {champ && (
              <p className="trophy__meta">
                {champ.teamName && <span>{champ.teamName}</span>}
                {champ.hometown && <span>from {champ.hometown}</span>}
                <span>
                  {champ.points} points · {champ.wins} win{champ.wins === 1 ? '' : 's'}
                </span>
                <span>{champ.titles > 1 ? `his ${ordinal(champ.titles)} title` : 'his first title'}</span>
              </p>
            )}
            {review.decider && <p className="trophy__decider">{review.decider.text}</p>}
          </div>
          {champ && card && (
            <div className="trophy__car">
              <CarTopDown
                livery={liveryForDriver(champ.driverId, team?.teamId ?? card.teamId, team ? team.driverIds.indexOf(champ.driverId) : undefined, undefined, card.make)}
                carNumber={card.carNumber}
                size={180}
              />
            </div>
          )}
        </div>
        {review.finalFour.length > 0 && (
          <div className="trophy__four">
            <h3>The final four</h3>
            <ol>
              {review.finalFour.map((f) => (
                <li key={f.driverId} className={f.champion ? 'is-champ' : undefined}>
                  <Name id={f.driverId} name={f.name} />
                  <span className="dim">
                    {f.finish !== null ? ` · ${ordinal(f.finish)} in the title race` : ' · did not start the title race'}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </section>

      <section className="yreview" aria-labelledby="yreview-h">
        <header className="yreview__head">
          <p className="yreview__kicker">Season {seasonNumber}</p>
          <h2 id="yreview-h">The year in review</h2>
          <p className="yreview__sub">
            {review.racesRun} races · {review.differentWinners} different winners
          </p>
        </header>

        {review.awards.length > 0 && (
          <div className="yreview__block">
            <h3>The awards</h3>
            <ul className="awards">
              {review.awards.map((a) => (
                <li key={a.kind} className={`award award--${a.kind}`}>
                  <span className="award__title">{a.title}</span>
                  <span className="award__name">
                    <Name id={a.driverId} name={a.name} />
                  </span>
                  <span className="award__basis">{a.basis}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="yreview__block">
          <h3>Records broken</h3>
          {review.recordsBroken.length > 0 ? (
            <ul className="yreview__list">
              {review.recordsBroken.map((r) => (
                <li key={r.title}>{r.text}</li>
              ))}
            </ul>
          ) : (
            <p className="dim">
              {seasons.filter((s) => s.seasonNumber < seasonNumber && s.complete !== false).length === 0
                ? 'The first season sets the marks; the next one can break them.'
                : 'No league record fell this season.'}
            </p>
          )}
        </div>

        <div className="yreview__block">
          <h3>Retirements</h3>
          {review.retirements.length > 0 ? (
            <ul className="yreview__list">
              {review.retirements.map((r) => (
                <li key={r.driverId}>
                  <Name id={r.driverId} name={r.name} />
                  {r.age !== null ? `, ${r.age},` : ''} retires after {r.seasons} season{r.seasons === 1 ? '' : 's'} — {r.starts} start
                  {r.starts === 1 ? '' : 's'}, {r.wins} win{r.wins === 1 ? '' : 's'}
                  {r.championships > 0 ? `, ${r.championships} title${r.championships === 1 ? '' : 's'}` : ''}.
                </li>
              ))}
            </ul>
          ) : (
            <p className="dim">Nobody retired this winter.</p>
          )}
        </div>

        {review.lost.length > 0 && (
          <div className="yreview__block yreview__block--lost">
            <h3>In memory</h3>
            <ul className="yreview__list">
              {review.lost.map((l) => (
                <li key={l.driverId}>{l.text}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="yreview__block">
          <h3>Hall of Fame</h3>
          {review.hallOfFame.length > 0 ? (
            <ul className="yreview__list">
              {review.hallOfFame.map((h) => (
                <li key={h.driverId}>
                  <Name id={h.driverId} name={h.name} /> goes in on {h.citation}.
                </li>
              ))}
            </ul>
          ) : (
            <p className="dim">No new inductees this winter.</p>
          )}
        </div>

        <div className="yreview__actions">
          {onReadSeasonPaper && (
            <button type="button" className="btn btn--quiet" onClick={onReadSeasonPaper}>
              Read the season edition
            </button>
          )}
          {onReadWinterPaper && (
            <button type="button" className="btn btn--quiet" onClick={onReadWinterPaper}>
              Read the winter edition
            </button>
          )}
          {onContinue && (
            <button type="button" className="btn btn--hot" onClick={onContinue}>
              On to the winter numbers
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
